import { useState } from 'react';
import AgencyDashboard from './AgencyDashboard';
import AgencyClients from './AgencyClients';
import AgencyPackages from './AgencyPackages';
import AgencyTasks from './AgencyTasks';
import AgencyLeads from './AgencyLeads';
import AgencyFinance from './AgencyFinance';
import ServiceManagement from '../clientModule/screens/ServiceManagement';
import { api } from '../lib/api';

export default function AgencyCrmSuite({ user, onBackToLegacy }) {
  const [activeSubTab, setActiveSubTab] = useState('dashboard');
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState('');

  const userRoleLower = (user?.role || '').toLowerCase();
  const isOwnerOrPartner = userRoleLower === 'owner' || userRoleLower === 'admin' || userRoleLower === 'partner';

  const handleSyncSheets = async () => {
    setSyncing(true);
    setSyncMessage('Syncing Agency CRM sheets...');
    try {
      const res = await api.post('/api/agency-crm/sync');
      setSyncMessage(res.message || 'Sheets synced successfully!');
      setTimeout(() => setSyncMessage(''), 3000);
    } catch (err) {
      setSyncMessage('Sync failed: ' + err.message);
      setTimeout(() => setSyncMessage(''), 4000);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div style={{
      display: 'flex',
      minHeight: '90vh',
      background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      borderRadius: 24,
      overflow: 'hidden',
      border: '1px solid #e2e8f0',
      boxShadow: '0 20px 40px rgba(0,0,0,0.02)'
    }}>
      {/* Sidebar for CRM Mode */}
      <aside style={{
        width: 250,
        background: 'rgba(255, 255, 255, 0.75)',
        backdropFilter: 'blur(20px)',
        borderRight: '1px solid #e2e8f0',
        padding: '24px 16px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        gap: 12
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* Logo / Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 8px', marginBottom: 20 }}>
            <span style={{ fontSize: '1.5rem' }}>🚀</span>
            <div>
              <div style={{ fontWeight: 900, fontSize: '1.02rem', color: '#0f172a', letterSpacing: '-0.02em' }}>Agency CRM</div>
              <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 700 }}>Enterprise Suite</div>
            </div>
          </div>

          {/* Navigation Buttons */}
          <button
            onClick={() => setActiveSubTab('dashboard')}
            style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', borderRadius: 12, border: 'none', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 800, textAlign: 'left',
              background: activeSubTab === 'dashboard' ? '#224eae' : 'transparent',
              color: activeSubTab === 'dashboard' ? '#fff' : '#475569'
            }}
          >
            📊 Dashboard
          </button>

          {isOwnerOrPartner && (
            <button
              onClick={() => setActiveSubTab('clients')}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', borderRadius: 12, border: 'none', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 800, textAlign: 'left',
                background: activeSubTab === 'clients' ? '#224eae' : 'transparent',
                color: activeSubTab === 'clients' ? '#fff' : '#475569'
              }}
            >
              👥 Clients Directory
            </button>
          )}
          {isOwnerOrPartner && (
            <button
              onClick={() => setActiveSubTab('services')}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', borderRadius: 12, border: 'none', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 800, textAlign: 'left',
                background: activeSubTab === 'services' ? '#224eae' : 'transparent',
                color: activeSubTab === 'services' ? '#fff' : '#475569'
              }}
            >
              🛠️ Service Catalog
            </button>
          )}

          {isOwnerOrPartner && (
            <button
              onClick={() => setActiveSubTab('packages')}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', borderRadius: 12, border: 'none', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 800, textAlign: 'left',
                background: activeSubTab === 'packages' ? '#224eae' : 'transparent',
                color: activeSubTab === 'packages' ? '#fff' : '#475569'
              }}
            >
              🎁 Package Builder
            </button>
          )}

          <button
            onClick={() => setActiveSubTab('tasks')}
            style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', borderRadius: 12, border: 'none', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 800, textAlign: 'left',
              background: activeSubTab === 'tasks' ? '#224eae' : 'transparent',
              color: activeSubTab === 'tasks' ? '#fff' : '#475569'
            }}
          >
            📋 Kanban Deliverables
          </button>

          {isOwnerOrPartner && (
            <button
              onClick={() => setActiveSubTab('leads')}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', borderRadius: 12, border: 'none', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 800, textAlign: 'left',
                background: activeSubTab === 'leads' ? '#224eae' : 'transparent',
                color: activeSubTab === 'leads' ? '#fff' : '#475569'
              }}
            >
              ⚡ Lead pipeline
            </button>
          )}

          {isOwnerOrPartner && (
            <button
              onClick={() => setActiveSubTab('finance')}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', borderRadius: 12, border: 'none', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 800, textAlign: 'left',
                background: activeSubTab === 'finance' ? '#224eae' : 'transparent',
                color: activeSubTab === 'finance' ? '#fff' : '#475569'
              }}
            >
              💳 Invoices & Payments
            </button>
          )}
        </div>

        {/* Bottom Actions */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {syncMessage && (
            <div style={{ fontSize: '0.78rem', background: '#e0f2fe', color: '#0369a1', padding: '8px 12px', borderRadius: 10, fontWeight: 700, border: '1px solid #bae6fd' }}>
              ℹ️ {syncMessage}
            </div>
          )}
          
          <button
            onClick={handleSyncSheets}
            disabled={syncing}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '10px 14px', borderRadius: 10, border: '1px solid #cbd5e1', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 800,
              background: '#fff', color: '#475569'
            }}
          >
            🔄 {syncing ? 'Syncing…' : 'Sync CRM Sheet'}
          </button>

          <button
            onClick={onBackToLegacy}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '10px 14px', borderRadius: 10, border: 'none', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 850,
              background: '#0f172a', color: '#fff'
            }}
          >
            ↩️ Back to Tracker
          </button>
        </div>
      </aside>

      {/* Main Canvas Workspace */}
      <main style={{
        flex: 1,
        padding: '32px 38px',
        maxHeight: '90vh',
        overflowY: 'auto'
      }}>
        {activeSubTab === 'dashboard' && <AgencyDashboard user={user} isOwnerOrPartner={isOwnerOrPartner} />}
        {activeSubTab === 'clients' && <AgencyClients user={user} />}
        {activeSubTab === 'packages' && <AgencyPackages />}
        {activeSubTab === 'services' && <ServiceManagement currency="₹" onServicesUpdated={() => {}} />}
        {activeSubTab === 'tasks' && <AgencyTasks />}
        {activeSubTab === 'leads' && <AgencyLeads />}
        {activeSubTab === 'finance' && <AgencyFinance />}
      </main>
    </div>
  );
}
